import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout, PageHero } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/motion";
import { ArrowLeft, Sparkles, Clock, Tag } from "lucide-react";
import DOMPurify from "dompurify";
import { openConsultationModal } from "../components/ConsultationModal";
import api from "../lib/api";
import { BLOGS, LEGAL_ENTITY } from "../data";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  const fixImgUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    return `${backendUrl}${url}`;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);

    api.get(`/blogs/${slug}`)
      .then((res) => {
        if (res.data) setPost(res.data);
      })
      .catch(() => {
        const fallback = BLOGS.find((b) => b.slug === slug);
        if (fallback) setPost(fallback);
      })
      .finally(() => setLoading(false));

    api.get("/blogs")
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setRelated(res.data.filter((b) => b.slug !== slug && b.published !== false).slice(0, 3));
        }
      })
      .catch(() => {
        setRelated(BLOGS.filter((b) => b.slug !== slug).slice(0, 3));
      });
  }, [slug]);

  const parseInlineMarkdown = (text) => {
    if (!text) return "";

    const imageMatch = text.match(/!\[(.*?)\]\((.*?)\)/);
    if (imageMatch) {
      const [full, alt, src] = imageMatch;
      const index = text.indexOf(full);
      return (
        <>
          {parseInlineMarkdown(text.substring(0, index))}
          <div className="my-6 rounded-2xl overflow-hidden shadow-sm bg-neutral-100">
            <img src={fixImgUrl(src)} alt={alt || "Article graphic"} className="w-full max-h-[420px] object-cover" />
            {alt && <p className="text-center text-xs text-neutral-400 py-2 bg-neutral-50 border-t border-black/5">{alt}</p>}
          </div>
          {parseInlineMarkdown(text.substring(index + full.length))}
        </>
      );
    }

    const linkMatch = text.match(/\[(.*?)\]\((.*?)\)/);
    if (linkMatch) {
      const [full, label, href] = linkMatch;
      const index = text.indexOf(full);
      return (
        <>
          {parseInlineMarkdown(text.substring(0, index))}
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#FF5A1F] font-semibold underline underline-offset-4 hover:text-[#e04f1a] transition-colors">{label}</a>
          {parseInlineMarkdown(text.substring(index + full.length))}
        </>
      );
    }

    const boldMatch = text.match(/\*\*(.*?)\*\*/);
    if (boldMatch) {
      const [full, boldText] = boldMatch;
      const index = text.indexOf(full);
      return (
        <>
          {parseInlineMarkdown(text.substring(0, index))}
          <strong className="font-bold text-[#0B0B0B]">{boldText}</strong>
          {parseInlineMarkdown(text.substring(index + full.length))}
        </>
      );
    }

    const italicMatch = text.match(/\*(.*?)\*/);
    if (italicMatch) {
      const [full, italicText] = italicMatch;
      const index = text.indexOf(full);
      return (
        <>
          {parseInlineMarkdown(text.substring(0, index))}
          <em className="italic text-neutral-800">{italicText}</em>
          {parseInlineMarkdown(text.substring(index + full.length))}
        </>
      );
    }

    return text;
  };

  const renderContent = (contentStr, excerptStr) => {
    if (!contentStr) {
      return (
        <div className="space-y-6">
          <p className="text-xl text-[#0B0B0B] font-medium leading-relaxed">{excerptStr}</p>
          <p className="leading-relaxed">At Neuravolt we believe great software is a compounding advantage. Whether you're shipping an MVP or modernizing a legacy platform, the principles of clarity, velocity, and engineering craft apply. Our teams pair AI-first thinking with rigorous software architecture to build products that scale effortlessly.</p>
        </div>
      );
    }

    // Check if content contains HTML formatted by Visual WYSIWYG editor
    const isHtml = /<[a-z][\s\S]*>/i.test(contentStr);
    if (isHtml) {
      return (
        <div className="space-y-6">
          {excerptStr && <p className="text-xl text-[#0B0B0B] font-medium leading-relaxed mb-6 border-b border-black/5 pb-6">{excerptStr}</p>}
          <div
            className="prose-nv space-y-4 text-neutral-700 text-base sm:text-lg leading-relaxed [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-[#0B0B0B] [&_h2]:mt-8 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-[#0B0B0B] [&_h3]:mt-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-[#FF5A1F] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:bg-[#FF5A1F]/5 [&_blockquote]:py-2 [&_img]:rounded-2xl [&_img]:max-h-[420px] [&_img]:w-full [&_img]:object-cover [&_img]:my-6 [&_a]:text-[#FF5A1F] [&_a]:font-semibold [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contentStr) }}
          />
        </div>
      );
    }

    const blocks = contentStr.split(/\n\s*\n/);
    return (
      <div className="space-y-6">
        {excerptStr && <p className="text-xl text-[#0B0B0B] font-medium leading-relaxed mb-6 border-b border-black/5 pb-6">{parseInlineMarkdown(excerptStr)}</p>}
        {blocks.map((block, idx) => {
          const trimmed = block.trim();
          if (!trimmed) return null;

          if (trimmed.startsWith("## ")) {
            return <h2 key={idx} className="font-display text-2xl font-bold text-[#0B0B0B] mt-10 mb-4">{parseInlineMarkdown(trimmed.replace(/^##\s*/, ""))}</h2>;
          }
          if (trimmed.startsWith("# ")) {
            return <h2 key={idx} className="font-display text-3xl font-bold text-[#0B0B0B] mt-12 mb-4">{parseInlineMarkdown(trimmed.replace(/^#\s*/, ""))}</h2>;
          }
          if (trimmed.startsWith("### ")) {
            return <h3 key={idx} className="font-display text-xl font-bold text-[#0B0B0B] mt-8 mb-3">{parseInlineMarkdown(trimmed.replace(/^###\s*/, ""))}</h3>;
          }
          if (trimmed.startsWith("> ")) {
            return <blockquote key={idx} className="border-l-4 border-[#FF5A1F] pl-5 italic text-neutral-800 my-6 py-3 bg-[#FF5A1F]/5 rounded-r-2xl text-lg">{parseInlineMarkdown(trimmed.replace(/^>\s*/, ""))}</blockquote>;
          }
          if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
            const listItems = trimmed.split("\n").map(l => l.replace(/^[\*\-\•]\s*/, ""));
            return (
              <ul key={idx} className="my-4 space-y-2.5 pl-2">
                {listItems.map((li, lIdx) => (
                  <li key={lIdx} className="flex items-start gap-2.5 text-neutral-700 text-base sm:text-lg leading-relaxed">
                    <span className="w-2 h-2 rounded-full bg-[#FF5A1F] mt-2.5 shrink-0" />
                    <span>{parseInlineMarkdown(li)}</span>
                  </li>
                ))}
              </ul>
            );
          }
          if (trimmed.startsWith("```")) {
            const codeText = trimmed.replace(/^```[a-z]*\n?/, "").replace(/```$/, "");
            return (
              <pre key={idx} className="bg-[#111827] text-amber-400 p-5 rounded-2xl overflow-x-auto text-xs sm:text-sm font-mono my-6 border border-black/10 shadow-inner">
                <code>{codeText}</code>
              </pre>
            );
          }

          return <p key={idx} className="leading-relaxed text-neutral-700 text-base sm:text-lg">{parseInlineMarkdown(trimmed)}</p>;
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="py-36 text-center text-neutral-400 font-medium">Loading article...</div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="py-36 text-center text-neutral-500 max-w-md mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#0B0B0B] mb-2 font-display">Article Not Found</h2>
          <p className="mb-6 text-sm">The article you are looking for may have been removed or updated.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#FF5A1F] font-semibold hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to all articles
          </Link>
        </div>
      </Layout>
    );
  }

  const postImg = post.img ? fixImgUrl(post.img) : undefined;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.seo_description || post.excerpt,
    ...(postImg ? { "image": postImg } : {}),
    ...(post.created_at ? { "datePublished": post.created_at } : (post.date ? { "datePublished": post.date } : {})),
    ...(post.updated_at ? { "dateModified": post.updated_at } : {}),
    "author": post.author
      ? (typeof post.author === "string" ? { "@type": "Person", "name": post.author } : post.author)
      : {
          "@type": "Organization",
          "name": "NeuraVolt Technology",
          "legalName": LEGAL_ENTITY,
          "url": "https://neuravolt.in"
        },
    "publisher": {
      "@type": "Organization",
      "name": "NeuraVolt Technology",
      "legalName": LEGAL_ENTITY,
      "url": "https://neuravolt.in",
      "logo": {
        "@type": "ImageObject",
        "url": "https://neuravolt.in/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://neuravolt.in/blog/${post.slug || slug}`
    }
  };

  return (
    <Layout>
      <Seo
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        keywords={post.seo_keywords || `${post.category || "AI"}, software development, Neuravolt`}
        path={`/blog/${post.slug || slug}`}
        image={postImg}
        jsonLd={jsonLd}
      />

      <PageHero eyebrow={`${post.category || "Insight"} · ${post.read || "5 min"} read`} title={post.title} />

      <article className="py-16 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-4 text-xs font-semibold text-neutral-400 mb-8 border-b border-black/5 pb-4">
            <span className="inline-flex items-center gap-1 text-[#FF5A1F] uppercase tracking-wider font-bold">
              <Tag className="w-3.5 h-3.5" /> {post.category || "General"}
            </span>
            <span>•</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> {post.read || "5 min"} read
            </span>
          </div>

          {post.img && (
            <Reveal>
              <div className="rounded-3xl overflow-hidden card-soft aspect-[16/9] mb-10 shadow-lg">
                <img src={fixImgUrl(post.img)} alt={post.title} className="w-full h-full object-cover" />
              </div>
            </Reveal>
          )}

          <div className="prose-nv">
            {renderContent(post.content, post.excerpt)}
          </div>

          <div className="mt-14 pt-8 border-t border-black/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#FF5A1F] font-semibold hover:underline text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to all articles
            </Link>
            <button onClick={() => openConsultationModal()} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF5A1F] text-white text-xs font-semibold hover:bg-[#ff6d38] transition-colors shadow-md">
              <Sparkles className="w-4 h-4" /> Discuss Your Project With Neuravolt
            </button>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="pb-24 px-5 sm:px-8 border-t border-black/5 pt-16 bg-[#FAF9F6]">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-display text-2xl font-bold text-[#0B0B0B] mb-8">More Articles</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((b) => (
                <Link key={b.id || b.slug} to={`/blog/${b.slug}`} className="card-soft rounded-3xl overflow-hidden group bg-white shadow-sm hover:shadow-md transition-all">
                  <div className="h-36 overflow-hidden bg-neutral-100">
                    {b.img && <img src={fixImgUrl(b.img)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="p-5">
                    <div className="text-xs text-[#FF5A1F] font-semibold mb-1">{b.category}</div>
                    <h3 className="font-heading font-semibold text-sm text-[#0B0B0B] leading-snug line-clamp-2">{b.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
